import * as MapActions from './map.actions';
import { Action, createReducer, on } from '@ngrx/store';
import { MapState, initialMapState } from './map.state';
import { ChangePositionHelper, FilterHelper, LoadingStateEnum } from '@tailormap-viewer/shared';
import { LayerTreeNodeHelper } from '../helpers/layer-tree-node.helper';
import { LayerModelHelper } from '../helpers/layer-model.helper';
import { AppLayerModel, LayerTreeNodeModel, ServiceModel } from '@tailormap-viewer/api';
import { AppLayerStateModel } from '../models';

type LayerTreeStateKey = 'baseLayerTreeNodes' | 'layerTreeNodes';

const getLayerTreeStateKey = (isBaseLayerTree?: boolean): LayerTreeStateKey => (isBaseLayerTree ? 'baseLayerTreeNodes' : 'layerTreeNodes');

const appendServices = (services: ServiceModel[], servicesToAdd: ServiceModel[]): ServiceModel[] => [ ...services, ...servicesToAdd ];

const appendServiceIfMissing = (services: ServiceModel[], service?: ServiceModel): ServiceModel[] => {
  if (!service || services.some(currentService => currentService.id === service.id)) {
    return services;
  }
  return [ ...services, service ];
};

const appendAppLayers = (layers: MapState['layers'], appLayers: AppLayerModel[]): MapState['layers'] => ([
  ...layers,
  ...appLayers.map(LayerModelHelper.getLayerWithInitialValues),
]);

const appendLayerTreeNodes = (
  layerTreeNodes: MapState['layerTreeNodes'],
  nodesToAdd: LayerTreeNodeModel[],
): MapState['layerTreeNodes'] => [ ...layerTreeNodes, ...nodesToAdd.map(node => LayerTreeNodeHelper.getExtendedLayerTreeNode(node, false)) ];

const placeNodeBeforeAfterSibling = (
  layerTreeNodes: MapState['layerTreeNodes'],
  nodeId: string,
  position: 'before' | 'after' = 'after',
  siblingLayerTreeNodeId?: string,
): MapState['layerTreeNodes'] => {
  const parentNodeIndex = siblingLayerTreeNodeId
    ? layerTreeNodes.findIndex(node => node.childrenIds?.includes(siblingLayerTreeNodeId))
    : layerTreeNodes.findIndex(node => node.root);
  if (parentNodeIndex === -1) {
    return layerTreeNodes;
  }
  return layerTreeNodes.map((node, index) => {
    if (index !== parentNodeIndex) {
      return node;
    }
    return {
      ...node,
      childrenIds: ChangePositionHelper.updateOrderInList(
        node.childrenIds ?? [],
        nodeId,
        position,
        siblingLayerTreeNodeId,
      ),
    };
  });
};

const updateLayer = (state: MapState, layerId: string, cb: (appLayer: AppLayerStateModel) => AppLayerStateModel) => {
  const layerIdx = state.layers.findIndex(layer => layer.id === layerId);
  if (layerIdx === -1) {
    return state;
  }
  return {
    ...state,
    layers: [
      ...state.layers.slice(0, layerIdx),
      cb({ ...state.layers[layerIdx] }),
      ...state.layers.slice(layerIdx + 1),
    ],
  };
};

const onLoadMap = (state: MapState): MapState => ({
  ...state,
  loadStatus: LoadingStateEnum.LOADING,
});

const onLoadMapSuccess = (
  state: MapState,
  payload: ReturnType<typeof MapActions.loadMapSuccess>,
): MapState => ({
  ...state,
  loadStatus: LoadingStateEnum.LOADED,
  mapSettings: {
    initialExtent: payload.initialExtent || undefined,
    maxExtent: payload.maxExtent || undefined,
    crs: payload.crs,
  },
  layers: payload.appLayers,
  services: payload.services,
  baseLayerTreeNodes: payload.baseLayerTreeNodes.map(node => LayerTreeNodeHelper.getExtendedLayerTreeNode(node, false)),
  layerTreeNodes: payload.layerTreeNodes,
  terrainLayerTreeNodes: payload.terrainLayerTreeNodes.map(node => LayerTreeNodeHelper.getExtendedLayerTreeNode(node, false)),
});

const onLoadMapFailed = (
  state: MapState,
  payload: ReturnType<typeof MapActions.loadMapFailed>,
): MapState => ({
  ...state,
  loadStatus: LoadingStateEnum.FAILED,
  errorMessage: payload.error,
});

const onSetLayerVisibility = (state: MapState, payload: ReturnType<typeof MapActions.setLayerVisibility>): MapState => ({
  ...state,
  layers: state.layers.map(layer => {
    const updated = payload.visibility.find(v => v.id === layer.id);
    if (!updated) {
      return layer;
    }
    return {
      ...layer,
      visible: updated.checked,
    };
  }),
});

const onToggleAllLayersVisibility = (
  state: MapState,
  payload: ReturnType<typeof MapActions.toggleAllLayersVisibility>,
): MapState => {
  // Maybe we should specify which layers are foreground/background layers in the state when fetched from the API
  const allForegroundLayerIds = new Set(LayerTreeNodeHelper.getAppLayerIds(state.layerTreeNodes, state.layerTreeNodes.find(l => l.root)));
  const filterTerms = payload.filterTerm
    ? FilterHelper.createFilterTerms(payload.filterTerm)
    : null;
  const foregroundLayers = state.layers.filter(l => {
    if (!allForegroundLayerIds.has(l.id)) {
      return false;
    }
    return filterTerms === null || FilterHelper.matchesFilterTerm(filterTerms, l.title);
  });
  const someVisible = foregroundLayers.some(l => l.visible);
  const filteredLayerIds = new Set(foregroundLayers.map(l => l.id));
  return {
    ...state,
    layers: state.layers.map(layer => {
      if (filteredLayerIds.has(layer.id)) {
        return {
          ...layer,
          visible: !someVisible,
        };
      }
      return layer;
    }),
  };
};

const onToggleSelectedLayerId = (state: MapState, payload: ReturnType<typeof MapActions.toggleSelectedLayerId>): MapState => ({
  ...state,
  selectedLayer: state.selectedLayer === payload.layerId ? undefined : payload.layerId,
});

const onToggleLevelExpansion = (state: MapState, payload: ReturnType<typeof MapActions.toggleLevelExpansion>): MapState => {
  const tree = getLayerTreeStateKey(payload.isBaseLayerTree);
  const idx = state[tree].findIndex(l => l.id === payload.id);
  if (idx === -1) {
    return state;
  }
  return {
    ...state,
    [tree]: [
      ...state[tree].slice(0, idx),
      { ...state[tree][idx], expanded: !state[tree][idx].expanded },
      ...state[tree].slice(idx + 1),
    ],
  };
};

const onAddServices = (state: MapState, payload: ReturnType<typeof MapActions.addServices>): MapState => ({
  ...state,
  services: appendServices(state.services, payload.services),
});

const onAddAppLayers = (state: MapState, payload: ReturnType<typeof MapActions.addAppLayers>): MapState => ({
  ...state,
  layers: appendAppLayers(state.layers, payload.appLayers),
});

const onAddLayerTreeNodes = (state: MapState, payload: ReturnType<typeof MapActions.addLayerTreeNodes>): MapState => {
  const tree = getLayerTreeStateKey(payload.isBaseLayerTree);
  return {
    ...state,
    [tree]: appendLayerTreeNodes(state[tree], payload.layerTreeNodes),
  };
};

const onAddLayerWithServicesToMap = (
  state: MapState,
  payload: ReturnType<typeof MapActions.addLayerWithServicesToMap>,
): MapState => {
  if (state.layers.some(layer => layer.id === payload.appLayer.id)) {
    return state;
  }
  const tree = getLayerTreeStateKey(payload.isBaseLayerTree);
  const services = appendServiceIfMissing(state.services, payload.service);
  const layers = appendAppLayers(state.layers, [payload.appLayer])
    .map(layer => {
      if (layer.id === payload.appLayer.id) {
        return {
          ...layer,
          visible: true,
        };
      }
      return layer;
    });

  if (!payload.layerTreeNode) {
    return {
      ...state,
      services,
      layers,
    };
  }

  const existingNode = state[tree].find(node => node.id === payload.layerTreeNode?.id || node.appLayerId === payload.appLayer.id);
  if (existingNode) {
    return {
      ...state,
      services,
      layers,
    };
  }

  const treeWithNode = appendLayerTreeNodes(state[tree], [payload.layerTreeNode]);
  const insertedNode = treeWithNode.find(node => node.id === payload.layerTreeNode?.id);
  const updatedTree = insertedNode
    ? placeNodeBeforeAfterSibling(treeWithNode, insertedNode.id, payload.position || 'after', payload.siblingLayerTreeNodeId)
    : treeWithNode;

  return {
    ...state,
    services,
    [tree]: updatedTree,
    layers,
    selectedLayer: payload.appLayer.id,
  };
};

const onRemoveAppLayer = (state: MapState, payload: ReturnType<typeof MapActions.removeAppLayer>): MapState => {
  const tree = getLayerTreeStateKey(payload.isBaseLayerTree);
  const nodeIdx = state[tree].findIndex(n => n.appLayerId === payload.appLayerId);
  const node = state[tree][nodeIdx];
  if (nodeIdx === -1 || !node) {
    return state;
  }
  const updatedTree = [
    ...state[tree].slice(0, nodeIdx),
    ...state[tree].slice(nodeIdx + 1),
  ].map(n => {
    if (n.childrenIds?.includes(node.id)) {
      return {
        ...n,
        childrenIds: n.childrenIds.filter(id => id !== node.id),
      };
    }
    return n;
  });
  return {
    ...state,
    layers: state.layers.filter(layer => layer.id !== payload.appLayerId),
    [tree]: updatedTree,
    selectedLayer: state.selectedLayer === payload.appLayerId ? undefined : state.selectedLayer,
  };
};

const onMoveLayerTreeNode = (state: MapState, payload: ReturnType<typeof MapActions.moveLayerTreeNode>): MapState => {
  const tree = getLayerTreeStateKey(payload.isBaseLayerTree);
  const newParentIdx = payload.parentId
    ? state[tree].findIndex(n => n.id === payload.parentId)
    : state[tree].findIndex(n => n.root);
  const draggedNode = state[tree].find(n => n.id === payload.nodeId);
  if (!draggedNode || payload.sibling === payload.nodeId || payload.parentId === payload.nodeId) {
    return state;
  }
  const childIds = LayerTreeNodeHelper.isLevelNode(draggedNode) ? LayerTreeNodeHelper.getChildNodeIds(state[tree], draggedNode) : [];
  if (childIds.includes(payload.sibling || '') || childIds.includes(payload.parentId || '')) {
    // don't drag level into itself or its children
    return state;
  }
  const currentParentIdx = state[tree].findIndex(n => n.childrenIds?.includes(payload.nodeId));
  return {
    ...state,
    [tree]: state[tree].map((node, idx) => {
      if (newParentIdx === idx) {
        return {
          ...node,
          childrenIds: ChangePositionHelper.updateOrderInList(node.childrenIds ?? [], payload.nodeId, payload.position, payload.sibling),
        };
      }
      if (currentParentIdx === idx) {
        return {
          ...node,
          childrenIds: node.childrenIds?.filter(id => id !== payload.nodeId) ?? [],
        };
      }
      return node;
    }),
  };
};


const onSetLayerTreeNodeChildren = (state: MapState, payload: ReturnType<typeof MapActions.setLayerTreeNodeChildren>): MapState => {
  const tree = getLayerTreeStateKey(payload.isBaseLayerTree);
  return {
    ...state,
    [tree]: state[tree].map(node => {
      const matches = payload.nodes.find(a => a.nodeId === node.id);
      if (matches !== undefined) {
        return {
          ...node,
          childrenIds: matches.children,
        };
      }
      return node;
    }),
  };
};

const onSetSelectedBackgroundNodeId = (state: MapState, payload: ReturnType<typeof MapActions.setSelectedBackgroundNodeId>): MapState => ({
  ...state,
  layers: state.layers.map(layer => {
    const parent = LayerTreeNodeHelper.getTopParent(state.baseLayerTreeNodes, layer);
    return {
      ...layer,
      visible: typeof parent === 'undefined'
        ? layer.visible
        : parent.id === payload.id,
    };
  }),
  selectedBackgroundNode: payload.id,
});

const onSetSelectedTerrainNodeId = (state: MapState, payload: ReturnType<typeof MapActions.setSelectedTerrainNodeId>): MapState => ({
  ...state,
  layers: state.layers.map(layer => {
    const parent = LayerTreeNodeHelper.getTopParent(state.terrainLayerTreeNodes, layer);
    return {
      ...layer,
      visible: typeof parent === 'undefined'
        ? layer.visible
        : parent.id === payload.id,
    };
  }),
  selectedTerrainLayerNode: payload.id,
});

const onSetLayerOpacity = (state: MapState, payload: ReturnType<typeof MapActions.setLayerOpacity>): MapState => ({
  ...state,
  layers: state.layers.map(layer => {
    const updated = payload.opacity.find(v => v.id === layer.id);
    if (!updated) {
      return layer;
    }
    return {
      ...layer,
      opacity: updated.opacity,
    };
  }),
});

const onSetLayerStyle = (state: MapState, payload: ReturnType<typeof MapActions.setLayerStyle>): MapState => ({
  ...state,
  layers: state.layers.map(layer => {
    const updated = payload.style.find(s => s.id === layer.id);
    if (!updated) {
      return layer;
    }
    return {
      ...layer,
      selectedStyleName: updated.style,
    };
  }),
});

const onAddLayerDetails = (
  state: MapState,
  payload: ReturnType<typeof MapActions.addLayerDetails>,
): MapState => ({
  ...state,
  layerDetails: [
    ...state.layerDetails,
    payload.layerDetails,
  ],
});

const onUpdateLayerTreeNodes = (
  state: MapState,
  payload: ReturnType<typeof MapActions.updateLayerTreeNodes>,
): MapState => ({
  ...state,
  layerTreeNodes: payload.layerTreeNodes,
});

const onToggleIn3DView = (state: MapState): MapState => ({
  ...state,
  in3dView: !state.in3dView,
});

const onUpdateTemporaryLayerName = (
  state: MapState,
  payload: ReturnType<typeof MapActions.updateTemporaryLayerName>,
): MapState => {
  return updateLayer(state, payload.id, layer => ({
    ...layer,
    temporaryLayerName: payload.temporaryLayerName,
  }));
};

const onUpdateAppLayerTitle = (
  state: MapState,
  payload: ReturnType<typeof MapActions.updateAppLayerTitle>,
): MapState => {
  return updateLayer(state, payload.id, layer => ({
    ...layer,
    title: payload.title,
  }));
};

const mapReducerImpl = createReducer<MapState>(
  initialMapState,
  on(MapActions.loadMap, onLoadMap),
  on(MapActions.loadMapSuccess, onLoadMapSuccess),
  on(MapActions.loadMapFailed, onLoadMapFailed),
  on(MapActions.setLayerVisibility, onSetLayerVisibility),
  on(MapActions.toggleAllLayersVisibility, onToggleAllLayersVisibility),
  on(MapActions.toggleLevelExpansion, onToggleLevelExpansion),
  on(MapActions.toggleSelectedLayerId, onToggleSelectedLayerId),
  on(MapActions.addServices, onAddServices),
  on(MapActions.addAppLayers, onAddAppLayers),
  on(MapActions.addLayerTreeNodes, onAddLayerTreeNodes),
  on(MapActions.addLayerWithServicesToMap, onAddLayerWithServicesToMap),
  on(MapActions.removeAppLayer, onRemoveAppLayer),
  on(MapActions.moveLayerTreeNode, onMoveLayerTreeNode),
  on(MapActions.setLayerTreeNodeChildren, onSetLayerTreeNodeChildren),
  on(MapActions.setSelectedBackgroundNodeId, onSetSelectedBackgroundNodeId),
  on(MapActions.setSelectedTerrainNodeId, onSetSelectedTerrainNodeId),
  on(MapActions.setLayerOpacity, onSetLayerOpacity),
  on(MapActions.setLayerStyle, onSetLayerStyle),
  on(MapActions.addLayerDetails, onAddLayerDetails),
  on(MapActions.updateLayerTreeNodes, onUpdateLayerTreeNodes),
  on(MapActions.toggleIn3dView, onToggleIn3DView),
  on(MapActions.updateTemporaryLayerName, onUpdateTemporaryLayerName),
  on(MapActions.updateAppLayerTitle, onUpdateAppLayerTitle),
);
export const mapReducer = (state: MapState | undefined, action: Action) => mapReducerImpl(state, action);
